import { useEffect, useRef } from 'react';
import * as PIXI from 'pixi.js';

import { ImageCoordinates, ViewportCoordinates } from 'lib/geometry';
import { useFloorplanLayerContext, Layer } from 'components/floorplan';

const AXIS_LINE_WIDTH_PX = 1;
const X_AXIS_COLOR = 0xff0000; // Red for X-axis
const Y_AXIS_COLOR = 0xff0000; // Red for Y-axis
const LABEL_FONT_SIZE = 12;
const LABEL_PADDING = 16;

const OriginMarkerLayer: React.FunctionComponent = () => {
  const context = useFloorplanLayerContext();
  const graphicsRef = useRef<PIXI.Graphics | null>(null);
  const xLabelRef = useRef<PIXI.Text | null>(null);
  const yLabelRef = useRef<PIXI.Text | null>(null);
  const cursorPositionRef = useRef<{ x: number; y: number } | null>(null);

  // Create the graphics object and text labels on mount
  useEffect(() => {
    const graphics = new PIXI.Graphics();
    graphics.name = 'origin-marker';

    const xLabelStyle = new PIXI.TextStyle({
      fontSize: LABEL_FONT_SIZE,
      fill: X_AXIS_COLOR,
      fontFamily: 'monospace',
    });
    const xLabel = new PIXI.Text('', xLabelStyle);
    xLabel.name = 'x-label';
    xLabel.visible = false;

    const yLabelStyle = new PIXI.TextStyle({
      fontSize: LABEL_FONT_SIZE,
      fill: Y_AXIS_COLOR,
      fontFamily: 'monospace',
    });
    const yLabel = new PIXI.Text('', yLabelStyle);
    yLabel.name = 'y-label';
    yLabel.visible = false;

    context.app.stage.addChild(graphics);
    context.app.stage.addChild(xLabel);
    context.app.stage.addChild(yLabel);

    graphicsRef.current = graphics;
    xLabelRef.current = xLabel;
    yLabelRef.current = yLabel;

    return () => {
      context.app.stage.removeChild(graphics);
      context.app.stage.removeChild(xLabel);
      context.app.stage.removeChild(yLabel);
      graphics.destroy();
      xLabel.destroy();
      yLabel.destroy();
      graphicsRef.current = null;
      xLabelRef.current = null;
      yLabelRef.current = null;
    };
  }, [context.app.stage]);

  // Track mouse movement on the canvas
  useEffect(() => {
    const canvas = context.app.view;

    const onMouseMove = (event: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      cursorPositionRef.current = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    };

    const onMouseLeave = () => {
      cursorPositionRef.current = null;
    };

    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseleave', onMouseLeave);

    return () => {
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [context.app.view]);

  return (
    <Layer
      onAnimationFrame={() => {
        if (!context.viewport.current || !graphicsRef.current) {
          return;
        }

        const viewport = context.viewport.current;
        const graphics = graphicsRef.current;
        const xLabel = xLabelRef.current;
        const yLabel = yLabelRef.current;

        // Get the origin position in viewport coordinates
        const originViewport = ImageCoordinates.toViewportCoordinates(
          context.floorplan.origin,
          viewport
        );

        // Clear and redraw
        graphics.clear();

        // Draw X-axis (horizontal line across entire viewport)
        graphics.lineStyle(AXIS_LINE_WIDTH_PX, X_AXIS_COLOR);
        graphics.moveTo(0, originViewport.y);
        graphics.lineTo(viewport.width, originViewport.y);

        // Draw Y-axis (vertical line across entire viewport)
        graphics.lineStyle(AXIS_LINE_WIDTH_PX, Y_AXIS_COLOR);
        graphics.moveTo(originViewport.x, 0);
        graphics.lineTo(originViewport.x, viewport.height);

        // Update coordinate labels based on cursor position
        if (cursorPositionRef.current && xLabel && yLabel) {
          const cursorViewport = ViewportCoordinates.create(
            cursorPositionRef.current.x,
            cursorPositionRef.current.y
          );

          const cursorFloorplan = ViewportCoordinates.toFloorplanCoordinates(
            cursorViewport,
            viewport,
            context.floorplan
          );

          // Update X label - positioned near the cursor
          xLabel.text = cursorFloorplan.x.toFixed(2);
          xLabel.x = cursorPositionRef.current.x + LABEL_PADDING;
          xLabel.y = cursorPositionRef.current.y + LABEL_PADDING;
          xLabel.visible = true;

          // Update Y label - positioned near the cursor (below X label)
          yLabel.text = cursorFloorplan.y.toFixed(2);
          yLabel.x = cursorPositionRef.current.x + LABEL_PADDING;
          yLabel.y =
            cursorPositionRef.current.y + LABEL_PADDING + LABEL_FONT_SIZE + 2;
          yLabel.visible = true;

          // Draw cursor tracking lines
          graphics.lineStyle(AXIS_LINE_WIDTH_PX, X_AXIS_COLOR, 0.5);
          graphics.moveTo(cursorPositionRef.current.x, originViewport.y);
          graphics.lineTo(
            cursorPositionRef.current.x,
            cursorPositionRef.current.y
          );

          graphics.lineStyle(AXIS_LINE_WIDTH_PX, Y_AXIS_COLOR, 0.5);
          graphics.moveTo(originViewport.x, cursorPositionRef.current.y);
          graphics.lineTo(
            cursorPositionRef.current.x,
            cursorPositionRef.current.y
          );
        } else if (xLabel && yLabel) {
          xLabel.visible = false;
          yLabel.visible = false;
        }
      }}
    />
  );
};

export default OriginMarkerLayer;
